import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, Globe } from 'lucide-react';

const Contact = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const subject = params.get('subject');
    if (subject) {
      setFormData(prev => ({ ...prev, subject }));
      // Scroll to contact section if subject is provided
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real application, you would send this data to your backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    });
    setTimeout(() => setSubmitted(false), 3000);
  };

  const contactInfo = [
    {
      icon: MapPin,
      label: 'Office Address',
      value: 'Ramasamy Plaza, First Floor No: 122/3, Plot No:1, KG Nanmai Nagar, Kovalan Nagar, Madurai - 625003',
       href:"https://www.google.com/maps/place/Mini+App+Tech/@9.895195866350704,78.10132931780082,17z/data=!3m1!4b1!4m6!3m5!1s0x834bc88d5395a74b:0x5fdf094635fa8c4!8m2!3d9.895195866350704!4d78.10132931780082",
      // href: 'https://www.google.com/maps/place/Mini+App+Tech/@9.8951939,78.101332,17z/data=!3m1!4b1!4m6!3m5!1s0x834bc88d5395a74b:0x5fdf094635fa8c4!8m2!3d9.8951939!4d78.101332',
    },
    {
      icon: MapPin,
      label: 'Registered Address',
      value: '54, POMBAN SWAMY NAGAR, Madurai-625003, India',
      // href: 'https://maps.google.com/?q=54+POMBAN+SWAMY+NAGAR+Madurai+625003',
    },
    {
      icon: Phone,
      label: 'Phone',
      value: '+91 8807683685',
      href: 'tel:+918807683685',
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'hr@miniapptech.in',
      href: 'mailto:hr@miniapptech.in',
    },
    {
      icon: Globe,
      label: 'Website',
      value: 'www.miniapptech.in',
      href: 'https://www.miniapptech.in',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <section id="contact" className="py-20 bg-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-dark mb-4">
            Get in <span className="text-primary">Touch</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have a project in mind? We'd love to hear from you. Reach out and let's discuss how we can help.
          </p>
        </motion.div>

        {/* Contact Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12"
        >
          {/* Contact Information */}
          <motion.div variants={itemVariants} className="space-y-8">
            <h3 className="text-2xl font-bold text-dark mb-6">Contact Information</h3>

            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              const contentBody = (
                <>
                  <motion.div
                    className="p-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg mt-1 group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-300"
                    whileHover={{ rotate: 10, scale: 1.1 }}
                  >
                    <Icon size={24} className="text-primary" />
                  </motion.div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">{info.label}</p>
                    <p className={`text-lg font-semibold text-dark leading-relaxed ${info.href ? 'hover:text-primary transition-colors' : ''}`}>
                      {info.value}
                    </p>
                  </div>
                </>
              );

              return info.href ? (
                <motion.a
                  key={index}
                  href={info.href}
                  target={info.href.startsWith('http') ? '_blank' : '_self'}
                  rel={info.href.startsWith('http') ? 'noopener noreferrer' : ''}
                  whileHover={{ x: 10 }}
                  className="flex items-start gap-4 group cursor-pointer"
                >
                  {contentBody}
                </motion.a>
              ) : (
                <motion.div
                  key={index}
                  whileHover={{ x: 10 }}
                  className="flex items-start gap-4 group"
                >
                  {contentBody}
                </motion.div>
              );
            })}

            {/* Additional Info */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="pt-8 border-t border-gray-200"
            >
              <h4 className="text-lg font-bold text-dark mb-3">Business Hours</h4>
              <p className="text-gray-600">Monday - Friday: 9:30 AM - 6:30 PM IST</p>
              <p className="text-gray-600">Saturday & Sunday: Closed</p>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div variants={itemVariants}>
            <form onSubmit={handleSubmit} className="space-y-5">
              <motion.div whileFocus={{ scale: 1.02 }}>
                <label className="block text-sm font-semibold text-dark mb-2">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                  placeholder="Your Name"
                />
              </motion.div>

              <motion.div whileFocus={{ scale: 1.02 }}>
                <label className="block text-sm font-semibold text-dark mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                  placeholder="your@email.com"
                />
              </motion.div>

              <motion.div whileFocus={{ scale: 1.02 }}>
                <label className="block text-sm font-semibold text-dark mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                  placeholder="+91 XXXX XXXX"
                />
              </motion.div>

              <motion.div whileFocus={{ scale: 1.02 }}>
                <label className="block text-sm font-semibold text-dark mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                  placeholder="Project Details"
                />
              </motion.div>

              <motion.div whileFocus={{ scale: 1.02 }}>
                <label className="block text-sm font-semibold text-dark mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 resize-none"
                  placeholder="Tell us about your project..."
                />
              </motion.div>

              {submitted && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2"
                >
                  <span>✓</span> Thank you! We'll get back to you soon.
                </motion.div>
              )}

              <motion.button
                whileHover={{ scale: 1.05, gap: '12px' }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                className="w-full bg-primary hover:bg-secondary text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2"
              >
                Send Message <Send size={20} />
              </motion.button>
            </form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;

